import React, { useEffect, useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { QBTestQuestionEto } from '../lib/api/types';

interface MobileQuestionNavProps {
  questions: QBTestQuestionEto[];
  currentIndex: number;
  totalQuestions?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
  onQuestionSelect: (index: number) => void;
  timedQuestionMode?: boolean;
  completedQuestions?: Set<string>;
}

const MobileQuestionNav: React.FC<MobileQuestionNavProps> = ({
  questions,
  currentIndex,
  onQuestionSelect,
  timedQuestionMode = false,
  completedQuestions = new Set()
}) => {
  const [isLowHeight, setIsLowHeight] = useState(false);

  // Check for low height landscape mode
  useEffect(() => {
    const checkOrientation = () => {
      const landscape = window.matchMedia('(orientation: landscape)').matches;
      const lowHeight = window.innerHeight < 500;
      setIsLowHeight(landscape && lowHeight);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);
  // Keyboard shortcuts support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't interfere with input fields
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, questions.length]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      // In TimedQuestion mode, prevent going back to completed questions
      if (timedQuestionMode) {
        const targetQuestion = questions[currentIndex - 1];
        if (targetQuestion && targetQuestion.questionId && completedQuestions.has(targetQuestion.questionId)) {
          return; // Don't allow navigation to completed questions
        }
      }
      onQuestionSelect(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      onQuestionSelect(currentIndex + 1);
    }
  };

  return (
    <>
      {isLowHeight ? (
        <>
          {/* Low Height Mode - Smaller Side Buttons with High Transparency Glassmorphism */}
          <div className="fixed left-0 bottom-20 z-50 flex flex-col gap-2">
            <button
              onClick={handlePrevious}
              className={`p-2 transition-all ${
                currentIndex === 0 || (timedQuestionMode && currentIndex > 0 && questions[currentIndex - 1]?.questionId && completedQuestions.has(questions[currentIndex - 1].questionId!))
                  ? 'opacity-30 cursor-not-allowed' 
                  : 'opacity-70 hover:opacity-100'
              }`}
              disabled={currentIndex === 0 || (timedQuestionMode && currentIndex > 0 && questions[currentIndex - 1]?.questionId && completedQuestions.has(questions[currentIndex - 1].questionId!)) || false}
              aria-label="Önceki soru"
            >
              <FaChevronUp className="w-5 h-5 text-gray-600" />
            </button>

            <button
              onClick={handleNext}
              className={`p-2 transition-all ${
                currentIndex === questions.length - 1 
                  ? 'opacity-30 cursor-not-allowed' 
                  : 'opacity-70 hover:opacity-100'
              }`}
              disabled={currentIndex === questions.length - 1}
              aria-label="Sonraki soru"
            >
              <FaChevronDown className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Normal Mode - Left/Right Buttons with High Transparency Glassmorphism */}
          <button
            onClick={handlePrevious}
            className={`fixed left-0 bottom-32 z-50 p-3 transition-all ${
              currentIndex === 0 || (timedQuestionMode && currentIndex > 0 && questions[currentIndex - 1]?.questionId && completedQuestions.has(questions[currentIndex - 1].questionId!)) 
                ? 'opacity-30 cursor-not-allowed' 
                : 'opacity-70 hover:opacity-100'
            }`}
            disabled={currentIndex === 0 || (timedQuestionMode && currentIndex > 0 && questions[currentIndex - 1]?.questionId && completedQuestions.has(questions[currentIndex - 1].questionId!)) || false}
            aria-label="Önceki soru"
          >
            <FaChevronLeft className="w-6 h-6 text-gray-600" />
          </button>

          <button
            onClick={handleNext}
            className={`fixed right-0 bottom-32 z-50 p-3 transition-all ${
              currentIndex === questions.length - 1 
                ? 'opacity-30 cursor-not-allowed' 
                : 'opacity-70 hover:opacity-100'
            }`}
            disabled={currentIndex === questions.length - 1}
            aria-label="Sonraki soru"
          >
            <FaChevronRight className="w-6 h-6 text-gray-600" />
          </button>
        </>
      )}
    </>
  );
};

export default MobileQuestionNav;